/**
 * Utility functions for comparing objects and generating diffs.
 */

export interface DiffResult {
  field: string;
  oldValue: any;
  newValue: any;
  hasChanged: boolean;
}

/**
 * Compare two objects and return a list of differences.
 *
 * @param original - The original object
 * @param modified - The modified object
 * @param excludeFields - Fields to exclude from comparison
 * @returns Array of field differences
 */
export function generateDiff(
  original: Record<string, any>,
  modified: Record<string, any>,
  excludeFields: string[] = [
    'id',
    'created_at',
    'updated_at',
    'import_id',
    'import_status',
    'search_complete',
    'technology_validation_complete',
    'validated_technologies',
    'invalid_technologies',
    'ImportId',
    'ImportStatus',
    'SearchComplete',
    'TechnologyValidationComplete',
    'ValidatedTechnologies',
    'InvalidTechnologies',
    'SimilarCompanies',
    'Technologies'
  ]
): DiffResult[] {
  const diffs: DiffResult[] = [];

  // Get all unique keys from both objects
  const allKeys = new Set([
    ...Object.keys(original),
    ...Object.keys(modified)
  ]);

  // Compare each field
  for (const key of allKeys) {
    // Skip excluded fields (case-insensitive comparison)
    if (excludeFields.some(field => field.toLowerCase() === key.toLowerCase())) {
      continue;
    }

    const oldValue = original[key];
    const newValue = modified[key];

    // Check if values are different
    const hasChanged = !isEqual(oldValue, newValue);

    diffs.push({
      field: key,
      oldValue,
      newValue,
      hasChanged
    });
  }

  return diffs;
}

/**
 * Get only the changed fields from a diff result.
 *
 * @param diffs - Array of diff results
 * @returns Array of only changed fields
 */
export function getChangedFields(diffs: DiffResult[]): DiffResult[] {
  return diffs.filter(diff => diff.hasChanged);
}

/**
 * Check if there are any changes between two objects.
 *
 * @param original - The original object
 * @param modified - The modified object
 * @param excludeFields - Fields to exclude from comparison
 * @returns True if there are changes, false otherwise
 */
export function hasChanges(
  original: Record<string, any>,
  modified: Record<string, any>,
  excludeFields: string[] = [
    'id',
    'created_at',
    'updated_at',
    'import_id',
    'import_status',
    'search_complete',
    'technology_validation_complete',
    'validated_technologies',
    'invalid_technologies',
    'ImportId',
    'ImportStatus',
    'SearchComplete',
    'TechnologyValidationComplete',
    'ValidatedTechnologies',
    'InvalidTechnologies',
    'SimilarCompanies',
    'Technologies'
  ]
): boolean {
  const diffs = generateDiff(original, modified, excludeFields);
  return getChangedFields(diffs).length > 0;
}

/**
 * Deep equality check for two values.
 * Handles primitives, arrays, and objects.
 *
 * @param a - First value
 * @param b - Second value
 * @returns True if values are equal, false otherwise
 */
export function isEqual(a: any, b: any): boolean {
  // Handle null/undefined
  if (a === b) return true;
  if (a == null || b == null) return false;

  // Handle different types
  if (typeof a !== typeof b) return false;

  // Handle arrays
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;

    // Sort arrays for comparison (to handle unordered arrays)
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();

    return sortedA.every((val, idx) => isEqual(val, sortedB[idx]));
  }

  // Handle objects
  if (typeof a === 'object' && typeof b === 'object') {
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);

    if (keysA.length !== keysB.length) return false;

    return keysA.every(key => isEqual(a[key], b[key]));
  }

  // Handle primitives
  return a === b;
}

/**
 * Format a value for display in the diff viewer.
 *
 * @param value - Value to format
 * @returns Formatted string representation
 */
export function formatValue(value: any): string {
  if (value == null) {
    return '(empty)';
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return '(empty)';
    }

    // Check if array contains objects
    if (value.some(item => typeof item === 'object' && item !== null)) {
      // Format as JSON for arrays of objects
      return JSON.stringify(value, null, 2);
    }

    // For simple arrays (strings, numbers, etc.), join with commas
    return value.join(', ');
  }

  if (typeof value === 'object') {
    // Pretty print objects
    return JSON.stringify(value, null, 2);
  }

  if (typeof value === 'string' && value.trim() === '') {
    return '(empty)';
  }

  return String(value);
}

/**
 * Convert a field name from snake_case to Title Case.
 *
 * @param fieldName - Field name in snake_case
 * @returns Field name in Title Case
 */
export function formatFieldName(fieldName: string): string {
  return fieldName
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
